import type {
  Project,
  ProjectDetail,
  SubmitRequest,
  UpdateProjectRequest,
  SubmitResponse,
  ApiError,
  PublicFramework,
  FrameworkConfig,
  AdminFrameworkConfig,
  FrameworkVersionSummary,
  SaveFrameworkRequest,
  PredefinedRedFlag,
  UserInfo,
  Capability,
  CreateCapabilityRequest,
  UpdateCapabilityRequest,
  Evaluation,
  SaveEvaluationRequest,
  UploadPdfRequest,
  PdfMetadata,
  PdfDownloadUrl,
  GapsFilters,
  GapsSummary,
  GapsTrend,
  GapRecord,
} from '../types';
const API_URL = import.meta.env.VITE_API_URL;

let getAccessToken: () => Promise<string | null> = async () => null;

export function setTokenGetter(getter: () => Promise<string | null>): void {
  getAccessToken = getter;
}

class ApiRequestError extends Error {
  status: number;
  data?: ApiError;

  constructor(status: number, data?: ApiError) {
    super(data?.message || `API Error: ${status}`);
    this.name = 'ApiRequestError';
    this.status = status;
    this.data = data;
  }
}

async function apiFetch(path: string, options?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options?.headers as Record<string, string>) || {}),
  };

  if (import.meta.env.VITE_AUTH_BYPASS === 'true') {
    // Local dev mode: send mock user headers
    headers['X-Local-User-Email'] = import.meta.env.VITE_LOCAL_USER_EMAIL || 'dev@localhost';
    headers['X-Local-User-Name'] = import.meta.env.VITE_LOCAL_USER_NAME || 'Local Developer';
    headers['X-Local-User-Role'] = import.meta.env.VITE_LOCAL_USER_ROLE || 'admin';
  } else {
    // Production mode: send Bearer token via MSAL.
    // null means "no signed-in account" — public endpoints (e.g. /framework) must
    // still work, so send without the header and let the server enforce authz.
    // A signed-in account whose token acquisition fails throws from getAccessToken,
    // failing the request instead of sending it unauthenticated.
    const token = await getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // SSRF false positive: browser fetch to our own API; API_URL is a build-time env
  // constant and `path` is always an internal literal, never a user-supplied URL.
  const response = await fetch(`${API_URL}${path}`, { // nosemgrep
    ...options,
    headers,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiRequestError(response.status, data);
  }

  return response;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await apiFetch(path, options);
  // Deletes return 204/empty bodies — only parse JSON when there is one
  if (response.status === 204) return undefined as unknown as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// Tagged template that URL-encodes interpolated path segments,
// so free-text values (template names, emails) round-trip safely
function p(strings: TemplateStringsArray, ...values: (string | number)[]): string {
  return strings.reduce(
    (out, s, i) => out + s + (i < values.length ? encodeURIComponent(String(values[i])) : ''),
    ''
  );
}

export const api = {
  // Projects
  getProjects: () => request<{ projects: Project[] }>('/projects').then(r => r.projects),
  getProject: (id: string) => request<ProjectDetail>(p`/projects/${id}`),
  submitProject: (data: SubmitRequest) =>
    request<SubmitResponse>('/submit', { method: 'POST', body: JSON.stringify(data) }),
  updateProject: (id: string, data: UpdateProjectRequest) =>
    request<Project>(p`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  submitForReview: (projectId: string) =>
    request<{ status: string; project_id: string }>(p`/projects/${projectId}/submit-for-review`, { method: 'POST' }),
  adminReEvaluate: (projectId: string) =>
    request<{ status: string; project_id: string }>(p`/admin/projects/${projectId}/re-evaluate`, { method: 'POST' }),
  deleteProject: (id: string) =>
    request<void>(p`/projects/${id}?confirm=true`, { method: 'DELETE' }),
  submitEvaluation: (projectId: string, data: SaveEvaluationRequest) =>
    request<Evaluation>(p`/projects/${projectId}/evaluations`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // PDF operations
  uploadPdf: (projectId: string, data: UploadPdfRequest) =>
    request<PdfMetadata>(p`/projects/${projectId}/pdf`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  removePdf: (projectId: string) =>
    request<void>(p`/projects/${projectId}/pdf`, { method: 'DELETE' }),
  getPdfDownloadUrl: (projectId: string) =>
    request<PdfDownloadUrl>(p`/projects/${projectId}/pdf`),

  // Framework (public)
  getPublicFramework: () => request<PublicFramework>('/framework'),

  // Framework Admin
  getActiveFramework: () => request<AdminFrameworkConfig>('/admin/framework'),
  listFrameworkVersions: () =>
    request<{ versions: FrameworkVersionSummary[] }>('/admin/framework/versions').then(r => r.versions),
  getFrameworkVersion: (version: string) =>
    request<FrameworkConfig>(p`/admin/framework/versions/${version}`),
  saveFramework: (data: SaveFrameworkRequest) =>
    request<FrameworkConfig>('/admin/framework', { method: 'POST', body: JSON.stringify(data) }),
  validateFramework: (config: FrameworkConfig) =>
    request<{ valid: boolean; errors: string[] }>('/admin/framework/validate', {
      method: 'POST',
      body: JSON.stringify({ config }),
    }),
  listFrameworkTemplates: () =>
    request<{ templates: string[] }>('/admin/framework/templates').then(r => r.templates),
  getFrameworkTemplate: (name: string) =>
    request<FrameworkConfig>(p`/admin/framework/templates/${name}`),
  saveFrameworkTemplate: (name: string, config: FrameworkConfig) =>
    request<{ message: string }>('/admin/framework/templates', {
      method: 'POST',
      body: JSON.stringify({ name, config }),
    }),
  deleteFrameworkTemplate: (name: string) =>
    request<{ message: string }>(p`/admin/framework/templates/${name}`, { method: 'DELETE' }),
  getRedFlags: () =>
    request<{ red_flags: PredefinedRedFlag[] }>('/admin/framework/red-flags').then(r => r.red_flags),

  // User Admin
  listUsers: () => request<{ users: UserInfo[] }>('/admin/users').then(r => r.users),
  updateUserRole: (userId: string, role: string) =>
    request<{ email: string; role: string }>(p`/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),
  updateUserStatus: (userId: string, status: string) =>
    request<{ email: string; status: string }>(p`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  // User Profile
  updateProfile: (data: { name: string }) =>
    request<{ message: string }>('/users/me/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Capabilities Admin
  listCapabilities: () =>
    request<{ capabilities: Capability[] }>('/admin/capabilities').then(r => r.capabilities),
  createCapability: (data: CreateCapabilityRequest) =>
    request<Capability>('/admin/capabilities', { method: 'POST', body: JSON.stringify(data) }),
  updateCapability: (id: string, data: UpdateCapabilityRequest) =>
    request<Capability>(p`/admin/capabilities/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCapability: (id: string) =>
    request<void>(p`/admin/capabilities/${id}`, { method: 'DELETE' }),

  // Gap insights endpoints
  getGapsSummary: (filters: GapsFilters) =>
    request<GapsSummary>(`/gaps/summary?${toQueryParams(filters)}`),

  getGapsTrends: (filters: GapsFilters) =>
    request<{ trends: GapsTrend[] }>(`/gaps/trends?${toQueryParams(filters)}`)
      .then(r => r.trends),

  getGapsDetails: (filters: GapsFilters & { limit?: number; last_key?: string }) =>
    request<{ gaps: GapRecord[]; next_key: string | null }>(
      `/gaps/details?${toQueryParams(filters)}`
    ),

  exportGapsCsv: async (filters: GapsFilters) => {
    const response = await apiFetch(`/gaps/export/csv?${toQueryParams(filters)}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `gap-insights-${filters.start_period}-to-${filters.end_period}.csv`);
    // Deferred revoke: Safari can cancel the download if revoked synchronously
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },

  exportGapsPdf: (filters: GapsFilters) =>
    request<{ download_url: string }>(`/gaps/export/pdf?${toQueryParams(filters)}`)
      .then(r => { triggerDownload(r.download_url); }),
};

// Anchor-click download: attaches auth via fetch upstream, keeps the SPA in place,
// and avoids popup blockers (unlike window.location.href / window.open after an await).
function triggerDownload(url: string, filename?: string): void {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function toQueryParams<T extends object>(obj: T): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  }
  return params;
}

export { ApiRequestError };
